import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/utils";
import type { HubInvestorProfileSummary } from "@/domain/hub";
import { ProfileIcon } from "./ProfileSwitcherShared";

interface ProfileSwitcherDropdownVariantProps {
	selectedProfile: HubInvestorProfileSummary;
	otherProfiles: HubInvestorProfileSummary[];
	hasMultipleProfiles: boolean;
	isOpen: boolean;
	toggleDropdown: () => void;
	handleProfileSwitch: (uuid: string) => void;
	getProfileTypeLabel: (type: string) => string;
	dropdownRef: React.RefObject<HTMLDivElement | null>;
	switchLabel: string;
	className?: string;
}

export function ProfileSwitcherDropdownVariant({
	selectedProfile,
	otherProfiles,
	hasMultipleProfiles,
	isOpen,
	toggleDropdown,
	handleProfileSwitch,
	getProfileTypeLabel,
	dropdownRef,
	switchLabel,
	className = "",
}: ProfileSwitcherDropdownVariantProps) {
	return (
		<div ref={dropdownRef} className={cn("relative", className)}>
			<button
				onClick={(e) => {
					e.stopPropagation();
					toggleDropdown();
				}}
				disabled={!hasMultipleProfiles}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label={switchLabel}
				className={cn(
					"w-full flex items-center justify-between rounded-md transition-all duration-200 shadow-sm border border-border-secondary p-3",
					hasMultipleProfiles
						? isOpen
							? "bg-hover-light rounded-t-none border-t-0 shadow-lg"
							: "bg-bg-primary hover:bg-hover-light hover:shadow-md hover:scale-102 cursor-pointer"
						: "bg-bg-primary cursor-default",
				)}
			>
				<div className="flex items-center space-x-2 flex-1 min-w-0">
					<div className="w-6 h-6 rounded-md overflow-visible flex-shrink-0 relative">
						<div className="w-full h-full bg-bg-secondary flex items-center justify-center rounded-md">
							<ProfileIcon
								type={selectedProfile.type}
								size={12}
								className="text-text-secondary"
							/>
						</div>
						<div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-success-bar-light rounded-full border border-bg-primary z-10"></div>
					</div>
					<div className="flex-1 min-w-0 text-left">
						<p className="text-xs font-medium text-text-primary truncate">
							{selectedProfile.display_name}
						</p>
						<p className="text-2xs text-text-secondary">
							{getProfileTypeLabel(selectedProfile.type)}
						</p>
					</div>
				</div>
				{hasMultipleProfiles && (
					<div className="flex flex-col items-center ml-1 flex-shrink-0">
						<ChevronUp
							size={10}
							className={cn(
								"text-text-secondary transition-opacity",
								isOpen ? "opacity-100" : "opacity-50",
							)}
						/>
						<ChevronDown
							size={10}
							className={cn(
								"text-text-secondary transition-opacity -mt-0.5",
								isOpen ? "opacity-50" : "opacity-100",
							)}
						/>
					</div>
				)}
			</button>

			{hasMultipleProfiles && isOpen && otherProfiles.length > 0 && (
				<div
					className="absolute bottom-full left-0 right-0 z-modal-backdrop"
					role="listbox"
					onKeyDown={(e) => e.stopPropagation()}
					onClick={(e) => e.stopPropagation()}
				>
					<div className="bg-bg-primary border border-border-secondary border-b-0 rounded-t-md shadow-lg max-h-60 overflow-auto">
						{otherProfiles.map((profile, index) => (
							<button
								key={profile.uuid}
								role="option"
								aria-selected={false}
								className={cn(
									"w-full flex items-center justify-between p-3 hover:bg-hover-light cursor-pointer transition-colors",
									index === 0 && "rounded-t-md",
								)}
								onClick={(e) => {
									e.stopPropagation();
									handleProfileSwitch(profile.uuid);
								}}
							>
								<div className="flex items-center space-x-2 flex-1 min-w-0">
									<div className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
										<div className="w-full h-full bg-bg-secondary flex items-center justify-center">
											<ProfileIcon
												type={profile.type}
												size={12}
												className="text-text-secondary"
											/>
										</div>
									</div>
									<div className="flex-1 min-w-0 text-left">
										<p className="text-xs font-medium text-text-primary truncate">
											{profile.display_name}
										</p>
										<p className="text-2xs text-text-secondary">
											{getProfileTypeLabel(profile.type)}
										</p>
									</div>
								</div>
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
